import type { RouteObject } from 'react-router'
import { WelcomePage } from '../features/welcome/WelcomePage'
import { memberRoutes } from '../features/member/routes'
import { consoleRoutes, officerRoutes, startGroupRoute } from '../features/officer/routes'
import { AppIndex, RequireOfficer, RequireSession } from '../session/RequireSession'
import { AppShell, type RouteHandle } from './AppShell'
import { MorePage } from './MorePage'
import { NotFound } from './NotFound'
import { NotificationsPage } from './NotificationsPage'
import { PageLoading } from './PageLoading'
import { RootError, RouteError } from './RouteError'

/**
 * Every route in the app. Public pages (welcome, the R-00 calculator, sign-in) use the
 * public shell; everything under /app needs a session and uses the signed-in shell with
 * the bottom bar for her role. Screens live in src/features/<area>/ and load lazily, so
 * the first download stays small on mobile data. The /app paths are the route map in
 * docs/PLAN.md (M15); notification links from the server point at them.
 */
export const routes: RouteObject[] = [
  {
    path: '/',
    Component: AppShell,
    ErrorBoundary: RootError,
    HydrateFallback: PageLoading,
    children: [
      {
        // Pathless: a screen that fails (a chunk that cannot load offline, say) shows its
        // error inside the shell, so the header, skip link and toasts stay.
        ErrorBoundary: RouteError,
        children: [
          { index: true, Component: WelcomePage },

          // R-00, public and needs no sign-in (PRD Stage 0: ships first).
          {
            path: 'calculator',
            handle: {
              offlineMessage: (t) => t('calculator.offline'),
            } satisfies RouteHandle,
            lazy: async () => {
              const { CalculatorPage } = await import('../features/calculator/CalculatorPage')
              return { Component: CalculatorPage }
            },
          },

          // M02 and M03 (phone, code, PIN) and M04 (rules summary and consent).
          {
            path: 'signin',
            lazy: async () => {
              const { SignInPage } = await import('../features/auth/SignInPage')
              return { Component: SignInPage }
            },
          },
          {
            path: 'join',
            lazy: async () => {
              const { JoinPage } = await import('../features/auth/JoinPage')
              return { Component: JoinPage }
            },
          },

          { path: '*', Component: NotFound },
        ],
      },
    ],
  },
  {
    path: '/app',
    Component: RequireSession,
    ErrorBoundary: RootError,
    HydrateFallback: PageLoading,
    children: [
      {
        ErrorBoundary: RouteError,
        children: [
          { index: true, Component: AppIndex },
          // Members: M05–M19 (bottom bar Home, Pay, Loans, Statement, More).
          ...memberRoutes,
          { path: 'more', Component: MorePage },
          {
            path: 'more/devices',
            lazy: async () => {
              const { DevicesPage } = await import('./DevicesPage')
              return { Component: DevicesPage }
            },
          },
          { path: 'notifications', Component: NotificationsPage },
          startGroupRoute,
          // Officers: O01–O08, O10, O12 (bottom bar Home, Approvals, Close, Members, More).
          { path: 'officer', Component: RequireOfficer, children: officerRoutes },
          // The web console at 1024 px and wider: O09, O11, O13.
          { path: 'console', Component: RequireOfficer, children: consoleRoutes },
          { path: '*', Component: NotFound },
        ],
      },
    ],
  },
]
